import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api';

// 创建 axios 实例，添加认证拦截器
const characterAxios = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 分钟超时，因为生成图片需要较长时间
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加 token
characterAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // 添加时间戳防止缓存
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now(),
      };
    }
    return config;
  },
  (error) => {
    console.error('请求错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
characterAxios.interceptors.response.use(
  (response) => {
    const res = response.data;
    console.log('[characterApi] 原始响应:', res);
    console.log('[characterApi] 拦截器准备返回:', res);

    // 后端 API 返回格式为 { code, message, data }
    if (res && res.code !== undefined) {
      if (res.code !== 200) {
        // 401: 未授权
        if (res.code === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
        console.log('[characterApi] 返回错误:', res.message);
        return Promise.reject(new Error(res.message || '请求失败'));
      }
      // 成功时返回完整结构 { code, message, data }
      console.log('[characterApi] 返回成功响应:', res);
      return res;
    }
    // 如果响应本身就是数组或对象（非标准格式），包装成标准格式返回
    const wrappedRes = {
      code: 200,
      message: '操作成功',
      data: res,
      timestamp: Date.now(),
    };
    console.log('[characterApi] 包装后返回:', wrappedRes);
    return wrappedRes;
  },
  (error) => {
    console.error('[characterApi] 响应错误:', error.message);
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API 响应类型
export interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
  timestamp?: number;
}

// 角色类型
export interface AvatarCharacter {
  id: string;
  name: string;
  description: string;
  appearancePrompt?: string;
  personalityPrompt?: string; // 人设提示词（System Prompt）
  baseSeed?: number;
  characterId: string;
  status: string;
  progress: number;
  currentExpression?: string;
  errorMessage?: string;
  expressionsJson?: string;
  selectedExpressions?: string;
  resolution?: string;
  modelName?: string;
  createdAt: string;
  updatedAt: string;
}

// 生成请求类型
export interface GenerateRequest {
  name: string;
  description: string;
  customPrompt?: string;
  personalityPrompt?: string; // 人设提示词（System Prompt）
  selectedExpressions?: string[];
  resolution?: string;
  baseSeed?: number;
  generateType?: string;
  referenceImageUrl?: string;
  modelName?: string;
  // 高级参数
  steps?: number;
  cfgScale?: number;
  samplerName?: string;
  restoreFaces?: boolean;
  enableHr?: boolean;
  hrScale?: number;
  denoisingStrength?: number;
  hrUpscaler?: string;
  batchSize?: number;
  nIter?: number;
  tiling?: boolean;
  // ControlNet 参数
  enableControlNet?: boolean;
  controlNetModel?: string;
  controlNetImageUrl?: string;
  controlNetWeight?: number;
  controlNetStartStep?: number;
  // LoRA 参数
  loraModels?: string[];
  // 图生图参数
  useImg2Img?: boolean;
  img2ImgSource?: string;
  img2ImgDenoisingStrength?: number;
}

// 进度类型
export interface ProgressInfo {
  taskId: string;
  characterId: string;
  name: string;
  status: string;
  progress: number;
  currentExpression?: string;
  errorMessage?: string;
  completedExpressions: string[];
  estimatedRemainingSeconds?: number;
  imageUrlList?: string[];
}

// 表情选项
export interface ExpressionOption {
  key: string;
  name: string;
}

// 分辨率选项
export interface ResolutionOption {
  value: string;
  label: string;
  description: string;
  estimatedTime: number;
}

// 模型选项
export interface ModelOption {
  value: string;
  label: string;
}

// 采样器选项
export interface SamplerOption {
  value: string;
  label: string;
}

// 放大算法选项
export interface UpscalerOption {
  value: string;
  label: string;
}

// ControlNet 模型选项
export interface ControlNetModelOption {
  value: string;
  label: string;
}

// LoRA 模型选项
export interface LoraOption {
  value: string;
  label: string;
}

// AI 分析结果类型
export interface AiAnalyzeResult {
  name: string;
  description: string;
  customPrompt: string;
  selectedExpressions: string[];
  resolution: string;
  modelName: string;
  steps: number;
  cfgScale: number;
  enableHr: boolean;
  restoreFaces: boolean;
  loraModels: string[];
  suggestion: string;
  fullParams: Record<string, any>;
}

// API 调用
export const characterApi = {
  // 创建生成任务
  generate: async (request: GenerateRequest): Promise<ApiResponse<{ taskId: string }>> => {
    const response = await characterAxios.post(`/admin/character/generate`, request);
    // 拦截器已经处理过响应，直接返回 response（拦截器返回的值就是 response）
    return response as any;
  },

  // 查询进度
  getProgress: async (taskId: string): Promise<ApiResponse<ProgressInfo>> => {
    const response = await characterAxios.get(`/admin/character/${taskId}/progress`);
    return response as any;
  },

  // 获取角色列表
  getList: async (): Promise<ApiResponse<AvatarCharacter[]>> => {
    const response = await characterAxios.get(`/admin/character/list`);
    return response as any;
  },

  // 获取角色详情
  getDetail: async (characterId: string, signal?: AbortSignal): Promise<ApiResponse<AvatarCharacter>> => {
    const response = await characterAxios.get(`/admin/character/${characterId}`, { signal });
    return response as any;
  },

  // 取消任务
  cancel: async (taskId: string): Promise<ApiResponse<void>> => {
    const response = await characterAxios.post(`/admin/character/${taskId}/cancel`);
    return response as any;
  },

  // 重新生成
  retry: async (characterId: string): Promise<ApiResponse<{ taskId: string }>> => {
    const response = await characterAxios.post(`/admin/character/${characterId}/retry`);
    return response as any;
  },

  // 删除角色
  delete: async (characterId: string): Promise<ApiResponse<void>> => {
    const response = await characterAxios.delete(`/admin/character/${characterId}`);
    return response as any;
  },

  // 获取表情选项
  getExpressions: async (): Promise<ApiResponse<ExpressionOption[]>> => {
    const response = await characterAxios.get(`/admin/character/expressions`);
    return response as any;
  },

  // 获取分辨率选项
  getResolutions: async (): Promise<ApiResponse<ResolutionOption[]>> => {
    const response = await characterAxios.get(`/admin/character/resolutions`);
    return response as any;
  },

  // 获取 SD 模型列表
  getModels: async (): Promise<ApiResponse<ModelOption[]>> => {
    const response = await characterAxios.get(`/admin/character/models`);
    return response as any;
  },

  // 获取 SD 采样器列表
  getSamplers: async (): Promise<ApiResponse<SamplerOption[]>> => {
    const response = await characterAxios.get(`/admin/character/samplers`);
    return response as any;
  },

  // 获取 SD 放大算法列表
  getUpscalers: async (): Promise<ApiResponse<UpscalerOption[]>> => {
    const response = await characterAxios.get(`/admin/character/upscalers`);
    return response as any;
  },

  // 获取 ControlNet 模型列表
  getControlNetModels: async (): Promise<ApiResponse<ControlNetModelOption[]>> => {
    const response = await characterAxios.get(`/admin/character/controlnet/models`);
    return response as any;
  },

  // 获取 LoRA 模型列表
  getLoras: async (): Promise<ApiResponse<LoraOption[]>> => {
    const response = await characterAxios.get(`/admin/character/loras`);
    return response as any;
  },

  // 检查 SD WebUI 状态
  getSdStatus: async (): Promise<ApiResponse<{
    status: 'running' | 'stopped';
    progress: number;
    eta: number;
    currentModel?: string;
  }>> => {
    const response = await characterAxios.get(`/admin/character/sd-status`);
    return response as any;
  },

  // AI 分析用户输入
  analyzeRequest: async (userInput: string, mode: 'simple' | 'advanced' = 'simple'): Promise<ApiResponse<AiAnalyzeResult>> => {
    const response = await characterAxios.post(`/admin/character/ai/analyze`, {
      userInput,
      mode,
    });
    return response as any;
  },

  // AI 聊天
  chat: async (userInput: string, conversationHistory?: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await characterAxios.post(`/admin/character/ai/chat`, {
      userInput,
      conversationHistory,
    });
    return response as any;
  },
};

export default characterApi;
